import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { convertBankAccountToIban } from '../utils/bankAccount';

export interface ConnectStatus {
  hasAccount: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsDue?: string;
  onboardingStartedAt?: string;
  selfBillingAccepted?: boolean;
}

interface Props {
  setError: (error: string) => void;
  /** Called once the initial status has loaded so the parent can decide what other payout UI to render. */
  onStatusLoaded?: (status: ConnectStatus) => void;
  /**
   * Gate for the onboarding redirect, so a parent with unsaved form data can confirm first.
   * Receives the redirect and runs it once the user agrees.
   */
  beforeRedirect?: (proceed: () => void) => void;
  /**
   * When set, the initial "Set up payouts" button is disabled and this message is shown instead.
   * Stripe is prefilled from the saved lender settings, so starting onboarding before they are
   * complete would send Stripe a half-empty address the user then has to retype.
   */
  startBlockedMessage?: string;
}

const runImmediately = (proceed: () => void) => proceed();

export const PayoutSetupSection: React.FC<Props> = ({
  setError,
  onStatusLoaded,
  beforeRedirect = runImmediately,
  startBlockedMessage,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bankAccountInput, setBankAccountInput] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const ibanPreview = useMemo(() => convertBankAccountToIban(bankAccountInput), [bankAccountInput]);
  const bankAccountValid = Boolean(ibanPreview);

  const load = useCallback(async () => {
    try {
      const res = await api.get<ConnectStatus>('/connect/status');
      setStatus(res.data);
      onStatusLoaded?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, t('connect.status_load_error')));
    } finally {
      setLoading(false);
    }
  }, [setError, t, onStatusLoaded]);

  useEffect(() => { load(); }, [load]);

  // Onboarding not finished yet (no account, or Stripe still missing details): the user goes
  // through the same form as at the start, including the bank account prefill.
  const showStartForm = status !== null && (!status.hasAccount || !status.detailsSubmitted);

  const startOnboarding = useCallback(() => {
    // The bank account is only asked for in the start form; later "Update" links go without it.
    const iban = showStartForm ? convertBankAccountToIban(bankAccountInput) : null;
    if (showStartForm && !iban) {
      // Shown inline under the field, see bank account input below.
      setSubmitAttempted(true);
      return;
    }

    beforeRedirect(async () => {
      setBusy(true);
      try {
        const res = await api.post<{ url: string }>('/connect/onboarding-link',
          iban ? { bankAccountNumber: iban } : {});
        window.location.href = res.data.url;
      } catch (err) {
        setError(getErrorMessage(err, t('connect.start_error')));
        setBusy(false);
      }
    });
  }, [showStartForm, bankAccountInput, beforeRedirect, setError, t]);

  const openDashboard = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.post<{ url: string }>('/connect/login-link');
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(getErrorMessage(err, t('connect.dashboard_error')));
    } finally {
      setBusy(false);
    }
  }, [setError, t]);

  if (loading) {
    return <p className="text-muted">{t('connect.loading')}</p>;
  }
  if (!status) {
    return null;
  }

  const requirementsList = (() => {
    if (!status.requirementsDue) return null;
    try {
      const parsed = JSON.parse(status.requirementsDue) as string[];
      if (!parsed.length) return null;
      return parsed;
    } catch {
      return null;
    }
  })();

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>{t('connect.section_title')}</h3>

      {showStartForm && (
        <>
          {status.hasAccount && (
            <div className="alert alert-warning">{t('connect.onboarding_incomplete')}</div>
          )}
          <p>{t('connect.intro')}</p>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label htmlFor="connect-bank-account">{t('connect.bank_account_label')}</label>
            <input
              id="connect-bank-account"
              type="text"
              value={bankAccountInput}
              onChange={e => setBankAccountInput(e.target.value)}
              placeholder={t('connect.bank_account_placeholder')}
              autoComplete="off"
              disabled={busy}
            />
            {(bankAccountInput || submitAttempted) && !bankAccountValid && (
              <div className="field-error" style={{ marginTop: '0.4rem' }}>{t('connect.bank_account_invalid')}</div>
            )}
            {bankAccountValid && (
              <div className="alert alert-info" style={{ marginTop: '0.5rem' }}>
                <strong>{t('connect.iban_preview_label')}</strong> {ibanPreview}
                <div style={{ marginTop: '0.25rem' }}>{t('connect.bank_account_shared_notice')}</div>
              </div>
            )}
          </div>
          {startBlockedMessage && (
            <div className="alert alert-warning" id="connect-start-blocked">{startBlockedMessage}</div>
          )}
          <button
            type="button"
            className={`btn btn-primary ${startBlockedMessage ? 'btn-blocked' : ''}`}
            onClick={startOnboarding}
            disabled={busy || !!startBlockedMessage}
            aria-describedby={startBlockedMessage ? 'connect-start-blocked' : undefined}
          >
            {busy && <span className="btn-spinner" aria-hidden="true" />}
            {t('connect.start_button')}
          </button>
        </>
      )}

      {status.hasAccount && status.detailsSubmitted && !status.chargesEnabled && (
        <>
          <div className="alert alert-warning">{t('connect.under_review')}</div>
          {requirementsList && (
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              {t('connect.requirements_due_label')}: {requirementsList.join(', ')}
            </p>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={startOnboarding}
            disabled={busy}
          >
            {busy && <span className="btn-spinner" aria-hidden="true" />}
            {t('connect.update_button')}
          </button>
        </>
      )}

      {status.hasAccount && status.chargesEnabled && status.payoutsEnabled && !status.selfBillingAccepted && (
        <div className="alert alert-warning" style={{ marginTop: '0.75rem' }}>
          {t('self_billing.required_before_listing')}
        </div>
      )}

      {status.hasAccount && status.chargesEnabled && status.payoutsEnabled && (
        <>
          <div className="alert alert-success">{t('connect.active')}</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={openDashboard}
              disabled={busy}
            >
              {t('connect.dashboard_button')}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={startOnboarding}
              disabled={busy}
            >
              {t('connect.update_button')}
            </button>
          </div>
        </>
      )}

      {status.hasAccount && status.chargesEnabled && !status.payoutsEnabled && (
        <>
          <div className="alert alert-warning">{t('connect.payouts_disabled')}</div>
          {requirementsList && (
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              {t('connect.requirements_due_label')}: {requirementsList.join(', ')}
            </p>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={startOnboarding}
            disabled={busy}
          >
            {t('connect.update_button')}
          </button>
        </>
      )}
    </div>
  );
};
